/**
 * Stammdaten pflegen — fünf Register in einer Tabelle.
 *
 * Die Ansicht zeichnet nur; gerechnet und geschrieben wird in `tabelle.js`.
 *
 * Zellen sind Text und werden erst beim Anklicken zum Eingabefeld. 995
 * Tauchgänge mal neun Spalten wären sonst neuntausend Eingabefelder — damit
 * bricht jeder Browser ein.
 */

import { SPEICHER, alleAus, alleTauchgaenge } from '../db.js';
import { GRUPPEN, REGISTER, register } from '../felder.js';
import { wort } from '../format.js';
import { umkehrung } from '../sammeln.js';
import { geraeteKennung } from '../sync.js';
import { baueZeilen, kartenaufbau, kartenkurz, pruefeSammelwert, rohwert, sammelSpalten,
  schreibeSammel, schreibeZelle, siebe, zellwert } from '../tabelle.js';
import { beiUmbruch, istSchmal } from '../mobil.js';
import { fuehreZusammen } from '../zusammenfuehren.js';
import { teileAuf } from '../aufteilen.js';
import { tauchgangszahl } from '../verortung.js';
import { frageAufteilen } from './aufteildialog.js';
import { zeigeErgebnis } from './ruecknahme.js';
import { oeffneSammelformular } from './sammelformular.js';

function reiter(aktiv) {
  const nav = document.createElement('nav');
  nav.className = 'register';
  for (const r of REGISTER) {
    const a = document.createElement('a');
    a.href = `#/stammdaten/${r.schluessel}`;
    a.textContent = r.titel;
    a.className = r.schluessel === aktiv ? 'aktiv' : '';
    nav.append(a);
  }
  return nav;
}

/**
 * Die Feldgruppen als Schalter — nur bei den Tauchgängen.
 *
 * Dreissig Spalten passen auf keinen Schirm, ein Spaltenwähler will gepflegt
 * werden. Die Gruppen gibt es im Formular ohnehin.
 */
function gruppenschalter(aktiv, beiWechsel) {
  const nav = document.createElement('nav');
  nav.className = 'gruppen';
  for (const g of GRUPPEN) {
    const knopf = document.createElement('button');
    knopf.type = 'button';
    knopf.className = g.schluessel === aktiv ? 'knopf aktiv' : 'knopf';
    knopf.textContent = g.titel;
    knopf.addEventListener('click', () => beiWechsel(g.schluessel));
    nav.append(knopf);
  }
  return nav;
}

function werkzeugleiste(wurzel, def, zustand) {
  const leiste = document.createElement('div');
  leiste.className = 'werkzeuge';
  leiste.innerHTML = `
    <input type="search" class="suche" placeholder="Suchen …" aria-label="Suchen">
    <label class="filter"><input type="checkbox" class="nur-unvollstaendig">
      nur unvollständige</label>
    <span class="trefferzahl"></span>
    <span class="sammelmeldung"></span>
    <div class="sammelleiste" hidden></div>`;

  leiste.querySelector('.suche').addEventListener('input', (e) => {
    zustand.text = e.target.value;
    zustand.markiert.clear();
    wurzel.zeichneNeu();
  });
  leiste.querySelector('.nur-unvollstaendig').addEventListener('change', (e) => {
    zustand.nurUnvollstaendige = e.target.checked;
    zustand.markiert.clear();
    wurzel.zeichneNeu();
  });
  return leiste;
}

/** Register, in denen zwei Sätze zu einem verschmolzen werden können. */
const VERSCHMELZBAR = ['sites', 'trips'];

/**
 * Lässt sich in diesem Register überhaupt etwas mit einer Markierung anfangen?
 *
 * Bei den Buddys nicht: Die einzige bearbeitbare Spalte ist der Name, und der
 * taugt nicht für eine Sammeländerung. Häkchen, die zu nichts führen, laden
 * nur zum Unfall ein.
 */
function markierbar(def) {
  return sammelSpalten(def.schluessel).length > 0 || VERSCHMELZBAR.includes(def.schluessel);
}

/**
 * Die Leiste erscheint erst, wenn etwas markiert ist.
 *
 * Angeboten werden nur Spalten, die für viele Sätze auf einmal Sinn ergeben —
 * gesperrte lassen sich auch hier nicht ändern, sonst wäre die Sperre in der
 * Zelle wirkungslos, und der Name gehört zu genau einem Satz.
 */
function aktualisiereSammelleiste(wurzel, def, zustand) {
  const leiste = wurzel.querySelector('.sammelleiste');
  const anzahl = zustand.markiert.size;
  leiste.hidden = anzahl === 0;
  if (anzahl === 0) { leiste.replaceChildren(); return; }

  const bearbeitbare = sammelSpalten(def.schluessel);
  const zahl = document.createElement('strong');
  zahl.textContent = `${anzahl} markiert`;

  if (def.schluessel === 'dives') {
    const bearbeiten = document.createElement('button');
    bearbeiten.type = 'button';
    bearbeiten.className = 'knopf haupt';
    bearbeiten.textContent = `${anzahl} bearbeiten`;
    bearbeiten.addEventListener('click', async () => {
      const markierte = [...zustand.markiert]
        .map((id) => wurzel.saetzeNachId.get(id)).filter(Boolean);
      const plaetze = await alleAus(wurzel.db, SPEICHER.sites);
      const ergebnis = await oeffneSammelformular(wurzel.db, markierte,
        wurzel.alleTauchgaenge, plaetze, { geraet: geraeteKennung() });
      if (!ergebnis) return;
      zustand.markiert.clear();
      await wurzel.aktualisiereDaten();
      zeigeErgebnis(wurzel.querySelector('.sammelmeldung'), wurzel.db, ergebnis,
        { geraet: geraeteKennung(), aufFrischen: () => wurzel.aktualisiereDaten() });
    });
    leiste.replaceChildren(zahl, bearbeiten);
    return;
  }

  const auswahl = document.createElement('select');
  auswahl.className = 'sammelfeld';
  for (const s of bearbeitbare) auswahl.append(new Option(s.beschriftung, s.schluessel));
  const wert = document.createElement('input');
  wert.className = 'sammelwert';
  wert.type = 'text';
  wert.placeholder = 'Wert';
  const knopf = document.createElement('button');
  knopf.type = 'button';
  knopf.className = 'knopf';
  knopf.textContent = 'Anwenden';

  knopf.addEventListener('click', () => anwenden(wurzel, def, zustand,
    auswahl.value, wert.value));

  const teile = bearbeitbare.length ? [zahl, auswahl, wert, knopf] : [zahl];

  // Zusammenfuehren ergibt nur bei genau zwei Saetzen Sinn — und nur dort, wo
  // Tauchgaenge ueber eine Kennung darauf zeigen. Buddys stehen als blosse
  // Namen im Tauchgang, das waere ein Umbenennen und keine Verschmelzung.
  if (anzahl === 2 && VERSCHMELZBAR.includes(def.schluessel)) {
    const verschmelzen = document.createElement('button');
    verschmelzen.type = 'button';
    verschmelzen.className = 'knopf verschmelzen';
    verschmelzen.textContent = 'Zusammenführen';
    verschmelzen.addEventListener('click', () => verschmelze(wurzel, def, zustand));
    teile.push(verschmelzen);
  }

  // Das Gegenstueck: Ein Platzsatz, in dem zwei Orte zusammengefallen sind.
  // Nur bei Plaetzen — nur dort haengen Tauchgaenge mit eigenem GPS daran,
  // und nur daran laesst sich messen, wo die Trennlinie liegt.
  if (anzahl === 1 && def.schluessel === 'sites') {
    const aufteilen = document.createElement('button');
    aufteilen.type = 'button';
    aufteilen.className = 'knopf aufteilen';
    aufteilen.textContent = 'Aufteilen';
    aufteilen.addEventListener('click', () => teile_auf(wurzel, zustand));
    teile.push(aufteilen);
  }

  leiste.replaceChildren(...teile);
}

/**
 * Eine Sammeländerung anwenden.
 *
 * Die Meldung steht in der Werkzeugleiste, nicht in der Sammelleiste: Die
 * Leiste verschwindet, sobald die Markierung aufgehoben ist — die Meldung
 * daneben wäre nie zu lesen.
 */
async function anwenden(wurzel, def, zustand, spaltenSchluessel, wert) {
  const meldung = wurzel.querySelector('.sammelmeldung');
  const spalteDef = def.spalten.find((s) => s.schluessel === spaltenSchluessel);
  const betroffen = [...zustand.markiert]
    .map((id) => wurzel.saetzeNachId.get(id)).filter(Boolean);

  try {
    pruefeSammelwert(def.schluessel, spaltenSchluessel, wert);
  } catch (fehler) {
    meldung.textContent = fehler.message;
    return;
  }

  // Dieselbe Form wie beim Sammelformular: ein Feld je Satz. Nur so gibt es
  // auch hier einen Weg zurück. Zeilen, die den Wert schon tragen, fallen
  // heraus — und die Rückfrage zählt dieselbe Liste, sonst beantwortete
  // "für 12 Zeilen setzen?" sich hinterher mit "9 geschrieben.".
  const aenderungen = betroffen
    .map((satz) => ({ satz,
      felder: [{ schluessel: spaltenSchluessel, wert,
        vorher: String(satz[spaltenSchluessel] ?? '') }] }))
    .filter(({ satz }) => String(satz[spaltenSchluessel] ?? '') !== String(wert));

  if (aenderungen.length === 0) {
    meldung.textContent = `${spalteDef.beschriftung} steht dort überall schon `
      + `auf "${wert}" — nichts zu schreiben.`;
    return;
  }

  const frage = `${spalteDef.beschriftung} für ${aenderungen.length} Zeilen `
    + `auf "${wert}" setzen?`;
  if (!globalThis.confirm(frage)) return;

  meldung.textContent = 'Schreibt …';
  try {
    const ergebnis = await schreibeSammel(wurzel.db, def.schluessel, aenderungen,
      { geraet: geraeteKennung(),
        melde: (fertig, gesamt) => { meldung.textContent = `${fertig} von ${gesamt} …`; } });

    const geschriebeneIds = new Set(ergebnis.staende.map((s) => s.id));
    const ruecknahme = geschriebeneIds.size
      ? { erwartet: Object.fromEntries(ergebnis.staende.map((s) => [s.id, s.geaendert])),
        [def.schluessel]: umkehrung(
          aenderungen.filter(({ satz }) => geschriebeneIds.has(satz.id))) }
      : null;

    zustand.markiert.clear();
    await wurzel.aktualisiereDaten();
    zeigeErgebnis(wurzel.querySelector('.sammelmeldung'), wurzel.db,
      { ...ergebnis, ruecknahme },
      { geraet: geraeteKennung(), aufFrischen: () => wurzel.aktualisiereDaten() });
  } catch (fehler) {
    wurzel.querySelector('.sammelmeldung').textContent = fehler.message;
  }
}

function kopfzeile(def, zeilen, zustand, wurzel, spalten) {
  const tr = document.createElement('tr');
  if (markierbar(def)) {
    const alle = document.createElement('th');
    alle.className = 'markierspalte';
    const haken = document.createElement('input');
    haken.type = 'checkbox';
    haken.title = 'alle sichtbaren markieren';
    haken.addEventListener('change', () => {
      for (const z of zeilen) {
        if (haken.checked) zustand.markiert.add(z.satz.id);
        else zustand.markiert.delete(z.satz.id);
      }
      wurzel.zeichneNeu();
    });
    alle.append(haken);
    tr.append(alle);
  }

  for (const s of spalten) {
    const th = document.createElement('th');
    th.textContent = s.beschriftung;
    if (!s.bearbeitbar) th.className = 'gesperrt';
    if (s.anker) th.classList.add('anker');
    tr.append(th);
  }
  return tr;
}

/**
 * Eine Zelle zum Bearbeiten öffnen.
 *
 * Gespeichert wird beim Verlassen, nicht bei jedem Tastendruck — und nur, wenn
 * sich etwas geändert hat. Schlägt es fehl, springt die Zelle auf den alten
 * Wert zurück; eine halb angekommene Änderung wäre schlimmer als eine sichtbar
 * abgelehnte.
 *
 * Angezeigt und bearbeitet wird nicht dasselbe: Eine Auswahlspalte zeigt
 * "Salzwasser" und speichert `salt`. Der Ausgangswert kommt deshalb aus dem
 * Satz, nie aus der Zelle.
 */
function oeffneZelle(db, def, zeile, s, td, wurzel, aufAenderung) {
  if (td.querySelector('input, select')) return;
  const angezeigt = td.textContent;
  const gespeichert = rohwert(def.schluessel, s.schluessel, zeile.satz);
  const alt = gespeichert === null || gespeichert === undefined ? '' : String(gespeichert);

  let eingabe;
  if (s.art === 'auswahl') {
    eingabe = document.createElement('select');
    eingabe.append(new Option('—', ''));
    for (const w of s.werte) eingabe.append(new Option(wort(w), w));
  } else {
    eingabe = document.createElement('input');
    eingabe.type = { datum: 'date', zahl: 'number' }[s.art] ?? 'text';
    if (s.schritt) eingabe.step = String(s.schritt);
    if (s.min !== undefined) eingabe.min = String(s.min);
    if (s.max !== undefined) eingabe.max = String(s.max);
  }
  eingabe.value = alt;
  td.replaceChildren(eingabe);
  eingabe.focus();

  let laeuft = false;
  const abschliessen = async () => {
    if (laeuft) return;
    laeuft = true;
    const neu = eingabe.value;
    if (neu === alt) { td.textContent = angezeigt; return; }
    try {
      const { satz } = await schreibeZelle(db, def.schluessel, zeile.satz, s.schluessel, neu,
        { geraet: geraeteKennung() });
      // Der geschriebene Stand wird in den Satz übernommen — und zwar in
      // denselben, auf den `saetzeNachId` und die Sammelleiste zeigen. Sonst
      // ginge die nächste Änderung derselben Zeile vom alten Stand aus und
      // machte die erste wieder rückgängig.
      Object.assign(zeile.satz, satz);
      zeile.werte[s.schluessel] = zellwert(def.schluessel, s.schluessel, zeile.satz,
        zeile.werte.anzahl ?? 0);
      td.textContent = zeile.werte[s.schluessel];
      td.classList.add('gespeichert');
      setTimeout(() => td.classList.remove('gespeichert'), 900);
      aufAenderung();
    } catch (fehler) {
      td.textContent = angezeigt;
      td.classList.add('schreibfehler');
      td.title = fehler.message;
      setTimeout(() => td.classList.remove('schreibfehler'), 2500);
    }
  };

  eingabe.addEventListener('blur', abschliessen);
  eingabe.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') eingabe.blur();
    if (e.key === 'Escape') { laeuft = true; td.textContent = angezeigt; }
  });
}

/**
 * Eine Zelle — als `<td>` in der Tabelle, als `<dd>` auf der Karte.
 *
 * Dasselbe Verhalten in beiden Gestalten: Text, der beim Antippen zum
 * Eingabefeld wird. Zwei Fassungen davon liefen unweigerlich auseinander,
 * sobald eine Prüfung dazukäme.
 */
function zelle(db, def, zeile, s, wurzel, aufAenderung, marke = 'td') {
  const td = document.createElement(marke);
  const wert = zeile.werte[s.schluessel];

  if (s.art === 'marke') {
    td.textContent = wert ? '✓' : '–';
    td.className = wert ? 'marke ja' : 'marke nein';
    if (s.anker) td.classList.add('anker');
    return td;
  }
  td.textContent = wert ?? '';
  if (!s.bearbeitbar) {
    td.className = 'gesperrt';
    if (s.anker) td.classList.add('anker');
    return td;
  }

  td.className = 'bearbeitbar';
  if (s.anker) td.classList.add('anker');
  td.tabIndex = 0;
  const oeffne = () => oeffneZelle(db, def, zeile, s, td, wurzel, aufAenderung);
  td.addEventListener('click', oeffne);
  td.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); oeffne(); }
  });
  return td;
}

function datenzeile(db, def, zeile, zustand, wurzel, aufAenderung, spalten) {
  const tr = document.createElement('tr');
  tr.dataset.id = zeile.satz.id;

  if (markierbar(def)) {
    const markieren = document.createElement('td');
    markieren.className = 'markierspalte';
    const haken = document.createElement('input');
    haken.type = 'checkbox';
    haken.checked = zustand.markiert.has(zeile.satz.id);
    haken.addEventListener('change', () => {
      if (haken.checked) zustand.markiert.add(zeile.satz.id);
      else zustand.markiert.delete(zeile.satz.id);
      aktualisiereSammelleiste(wurzel, def, zustand);
    });
    markieren.append(haken);
    tr.append(markieren);
  }

  for (const s of spalten) {
    tr.append(zelle(db, def, zeile, s, wurzel, aufAenderung));
  }
  return tr;
}

/**
 * Ein Satz als Karte.
 *
 * Die Tabelle blendete auf schmalen Schirmen alle Spalten ausser den Ankern
 * aus — bei den Plätzen blieb damit der Name und sonst nichts, und bearbeiten
 * liess sich unterwegs gar nichts mehr. Die Karte dreht die Tabelle um neunzig
 * Grad: je Satz ein Kasten, je Spalte eine Zeile darin, und jede davon bleibt
 * antippbar.
 */
function karte(db, def, zeile, zustand, wurzel, aufAenderung, aufbau) {
  const li = document.createElement('li');
  li.className = 'stammkarte';
  li.dataset.id = zeile.satz.id;
  li.classList.toggle('markiert', zustand.markiert.has(zeile.satz.id));

  if (markierbar(def)) {
    const haken = document.createElement('input');
    haken.type = 'checkbox';
    haken.className = 'stammkarte-haken';
    haken.checked = zustand.markiert.has(zeile.satz.id);
    haken.setAttribute('aria-label', 'Satz markieren');
    haken.addEventListener('change', () => {
      if (haken.checked) zustand.markiert.add(zeile.satz.id);
      else zustand.markiert.delete(zeile.satz.id);
      li.classList.toggle('markiert', haken.checked);
      aktualisiereSammelleiste(wurzel, def, zustand);
    });
    li.append(haken);
  }

  // Zugeklappt: Name und eine Zeile Auskunft. Aufgeklappt: die Felder zum
  // Bearbeiten. `<details>` statt eines eigenen Schalters — das Aufklappen
  // ohne Skript zu bekommen ist hier gratis, samt Tastatur und Vorlesen.
  const klapp = document.createElement('details');
  klapp.className = 'stammkarte-klapp';

  const kopf = document.createElement('summary');
  const titel = document.createElement('h3');
  titel.className = 'stammkarte-titel';
  titel.textContent = aufbau.titel ? (zeile.werte[aufbau.titel.schluessel] ?? '—') : '—';
  kopf.append(titel);

  const unterzeile = aufbau.unter.length
    ? aufbau.unter.map((s) => zeile.werte[s.schluessel]).filter(Boolean).join(' · ')
    : kartenkurz(aufbau, zeile.werte);
  if (unterzeile) {
    const unter = document.createElement('p');
    unter.className = 'stammkarte-unter';
    unter.textContent = unterzeile;
    kopf.append(unter);
  }
  klapp.append(kopf);

  const felder = document.createElement('dl');
  felder.className = 'stammkarte-felder';
  for (const s of aufbau.felder) {
    const reihe = document.createElement('div');
    reihe.className = 'stammkarte-feld';
    const dt = document.createElement('dt');
    dt.textContent = s.beschriftung;
    reihe.append(dt, zelle(db, def, zeile, s, wurzel, aufAenderung, 'dd'));
    felder.append(reihe);
  }
  klapp.append(felder);
  li.append(klapp);
  return li;
}

function karten(db, def, zeilen, zustand, wurzel, aufAenderung, spalten) {
  const aufbau = kartenaufbau(def.schluessel, spalten);
  const huelle = document.createDocumentFragment();

  // Was in der Tabelle im Spaltenkopf steht, braucht auf den Karten eine
  // eigene Zeile: alle sichtbaren auf einmal markieren.
  if (markierbar(def) && zeilen.length > 1) {
    const kopf = document.createElement('label');
    kopf.className = 'stammkarten-kopf';
    const haken = document.createElement('input');
    haken.type = 'checkbox';
    haken.checked = zeilen.every((z) => zustand.markiert.has(z.satz.id));
    haken.addEventListener('change', () => {
      for (const z of zeilen) {
        if (haken.checked) zustand.markiert.add(z.satz.id);
        else zustand.markiert.delete(z.satz.id);
      }
      wurzel.zeichneNeu();
    });
    const wort = document.createElement('span');
    wort.textContent = `alle ${zeilen.length} markieren`;
    kopf.append(haken, wort);
    huelle.append(kopf);
  }

  const liste = document.createElement('ul');
  liste.className = 'stammkarten';
  for (const zeile of zeilen) {
    liste.append(karte(db, def, zeile, zustand, wurzel, aufAenderung, aufbau));
  }
  huelle.append(liste);
  return huelle;
}

function tabelle(db, def, zeilen, zustand, wurzel, aufAenderung, spalten) {
  const tab = document.createElement('table');
  // Nur die Tauchgänge haben Ankerspalten. Die schmale Ansicht darf deshalb
  // auch nur dort auf sie eindampfen — sonst bliebe von Plätzen, Reisen und
  // Ausrüstung die Markierspalte übrig und von den Buddys gar nichts.
  tab.className = def.schluessel === 'dives' ? 'stammtabelle tauchgaenge' : 'stammtabelle';
  const kopf = document.createElement('thead');
  kopf.append(kopfzeile(def, zeilen, zustand, wurzel, spalten));
  tab.append(kopf);

  const koerper = document.createElement('tbody');
  for (const zeile of zeilen) {
    koerper.append(datenzeile(db, def, zeile, zustand, wurzel, aufAenderung, spalten));
  }
  tab.append(koerper);
  return tab;
}

export async function ansichtStammdaten(db, registerSchluessel, aufAenderung) {
  const def = register(registerSchluessel) ?? REGISTER[0];
  const wurzel = document.createElement('section');
  wurzel.className = 'stammdaten';
  wurzel.append(reiter(def.schluessel));

  // `tauchgaenge` bleibt über die Lebenszeit der Ansicht dieselbe Array-Referenz
  // und wird in aktualisiereDaten() in-place aufgefrischt (nie neu zugewiesen) —
  // so zeigen `wurzel.alleTauchgaenge` und `baueZeilen()` (für die
  // Tauchgangszahl je Platz/Reise) immer den aktuellen Stand, auch nach einer
  // Sammel- oder Zellenänderung in einem anderen Register. Im Register
  // *dives* ist `saetze` dieselbe Referenz wie `tauchgaenge`: Ein Auffrischen
  // genügt dort für beide.
  const tauchgaenge = await alleTauchgaenge(db);
  const saetze = def.schluessel === 'dives'
    ? tauchgaenge
    : await alleAus(db, SPEICHER[def.schluessel]);
  const zustand = { text: '', nurUnvollstaendige: false, markiert: new Set() };

  wurzel.db = db;
  wurzel.saetzeNachId = new Map(saetze.map((s) => [s.id, s]));
  wurzel.alleTauchgaenge = tauchgaenge;

  wurzel.append(werkzeugleiste(wurzel, def, zustand));
  const behaelter = document.createElement('div');
  behaelter.className = 'tabellenbereich';
  wurzel.append(behaelter);

  zustand.gruppe = 'kern';

  // Ankerspalten immer, dazu die der gewählten Gruppe. Register ohne
  // Gruppenangabe (Plätze, Reisen, Buddys, Ausrüstung) zeigen alles.
  const sichtbareSpalten = () => (def.schluessel === 'dives'
    ? def.spalten.filter((s) => s.anker || s.gruppe === zustand.gruppe)
    : def.spalten);

  const zeichne = () => {
    const spalten = sichtbareSpalten();
    const zeilen = siebe(baueZeilen(def.schluessel, saetze, tauchgaenge, spalten),
      def.schluessel, zustand);
    // Die Entscheidung faellt beim Zeichnen, nicht beim Bauen: Wer das Fenster
    // schmaler zieht, bekommt die Karten, ohne die Ansicht zu verlassen.
    behaelter.classList.toggle('karten', istSchmal());
    behaelter.replaceChildren(istSchmal()
      ? karten(db, def, zeilen, zustand, wurzel, aufAenderung, spalten)
      : tabelle(db, def, zeilen, zustand, wurzel, aufAenderung, spalten));
    wurzel.querySelector('.trefferzahl').textContent = `${zeilen.length} von ${saetze.length}`;
    aktualisiereSammelleiste(wurzel, def, zustand);
  };
  wurzel.zeichneNeu = zeichne;

  // Nach einer Sammeländerung müssen die Sätze neu gelesen werden — sonst
  // zeigte die Tabelle weiter die alten Werte. `tauchgaenge` wird dabei immer
  // mitaufgefrischt, nicht nur im Register *dives*: Sonst blieben die
  // Vorschlagslisten und die Tauchgangszahl je Platz/Reise im Sammelformular
  // und in der Tabelle für den Rest der Sitzung auf dem Stand des Mount.
  wurzel.aktualisiereDaten = async () => {
    const frischeTauchgaenge = await alleTauchgaenge(db);
    tauchgaenge.length = 0;
    tauchgaenge.push(...frischeTauchgaenge);
    if (def.schluessel !== 'dives') {
      const frisch = await alleAus(db, SPEICHER[def.schluessel]);
      saetze.length = 0;
      saetze.push(...frisch);
    }
    wurzel.saetzeNachId = new Map(saetze.map((s) => [s.id, s]));
    aufAenderung();
    zeichne();
  };

  // Der Schalter zeichnet sich selbst neu, damit die Hervorhebung mitwandert.
  // Er gehört vor die Werkzeugleiste, wird aber erst hier gebaut: Vorher gibt
  // es `zeichne` noch nicht.
  if (def.schluessel === 'dives') {
    const wechsle = (gruppe) => {
      zustand.gruppe = gruppe;
      wurzel.querySelector('.gruppen').replaceWith(gruppenschalter(gruppe, wechsle));
      zeichne();
    };
    wurzel.insertBefore(gruppenschalter(zustand.gruppe, wechsle),
      wurzel.querySelector('.werkzeuge'));
  }

  // Beim Wechsel der Breite wird neu gezeichnet — sonst bliebe die Tabelle
  // stehen, bis jemand das Register wechselt.
  const loeseUmbruch = beiUmbruch(() => zeichne());
  wurzel.aufraeumen = () => loeseUmbruch();

  zeichne();
  return wurzel;
}

/**
 * Einen Platzsatz aufteilen, in dem zwei Orte zusammengefallen sind.
 *
 * Das Gegenstück zum Zusammenführen und harmloser als dieses: Es verschwindet
 * kein Stammsatz, ein zweiter entsteht. Wer sich vertut, führt die beiden
 * wieder zusammen.
 */
async function teile_auf(wurzel, zustand) {
  const meldung = wurzel.querySelector('.sammelmeldung');
  const [kennung] = [...zustand.markiert];
  const platz = wurzel.saetzeNachId.get(kennung);
  if (!platz) return;

  const tauchgaenge = (await alleTauchgaenge(wurzel.db))
    .filter((tg) => tg.site_id === platz.id);
  if (tauchgaenge.length < 2) {
    meldung.textContent = 'Aufteilen braucht mindestens zwei Tauchgänge an diesem Platz.';
    return;
  }

  const wahl = await frageAufteilen(platz, tauchgaenge);
  if (!wahl) return;

  meldung.textContent = 'Teilt auf …';
  try {
    const ergebnis = await teileAuf(wurzel.db, platz, wahl.ids, wahl.name,
      { geraet: geraeteKennung(), leeren: wahl.leeren });
    zustand.markiert.clear();
    await wurzel.aktualisiereDaten();
    wurzel.querySelector('.sammelmeldung').textContent =
      `„${ergebnis.neu.name}" angelegt, ${tauchgangszahl(ergebnis.umgehaengt)} umgehängt`
      + (ergebnis.geleert ? ` · Position von „${platz.name}" geleert.` : '.');
  } catch (fehler) {
    wurzel.querySelector('.sammelmeldung').textContent = fehler.message;
  }
}

/**
 * Zwei Sätze verschmelzen.
 *
 * Gefragt wird, welcher bleibt — mit Namen, nicht mit "A oder B". Wer die
 * Rückfrage abbricht, hat nichts geändert.
 */
async function verschmelze(wurzel, def, zustand) {
  const meldung = wurzel.querySelector('.sammelmeldung');
  const [ersterId, zweiterId] = [...zustand.markiert];
  const erster = wurzel.saetzeNachId.get(ersterId);
  const zweiter = wurzel.saetzeNachId.get(zweiterId);
  if (!erster || !zweiter) return;

  const frage = `"${erster.name}" und "${zweiter.name}" zusammenführen.

`
    + `OK = "${erster.name}" bleibt, "${zweiter.name}" verschwindet.`;
  if (!globalThis.confirm(frage)) return;

  meldung.textContent = 'Führt zusammen …';
  try {
    const { umgehaengt } = await fuehreZusammen(wurzel.db, def.schluessel,
      erster, zweiter, { geraet: geraeteKennung() });
    zustand.markiert.clear();
    await wurzel.aktualisiereDaten();
    wurzel.querySelector('.sammelmeldung').textContent =
      `Zusammengeführt, ${umgehaengt} Tauchgänge umgehängt.`;
  } catch (fehler) {
    wurzel.querySelector('.sammelmeldung').textContent = fehler.message;
  }
}
